import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { startAutoTranslate } from "@/lib/i18n";

// Silence the benign "ResizeObserver loop completed with undelivered notifications"
// warning that triggers the CRA dev-server error overlay and blocks clicks in preview.
// Harmless in production but prevents reliable interactions on the preview URL.
if (typeof window !== "undefined") {
  const RO_MSG = /ResizeObserver loop( completed with undelivered notifications| limit exceeded)/;

  if (window.ResizeObserver) {
    const _RO = window.ResizeObserver;
    window.ResizeObserver = class extends _RO {
      constructor(cb) {
        super((...args) => {
          try { cb(...args); } catch (e) {
            if (!RO_MSG.test(String(e?.message || ""))) throw e;
          }
        });
      }
    };
  }

  // Stop the webpack-dev-server error overlay from surfacing this benign message.
  window.addEventListener("error", (e) => {
    if (e?.message && RO_MSG.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String(e?.reason?.message || e?.reason || "");
    if (RO_MSG.test(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);

  // Nasconde l'overlay del dev-server se dovesse comunque comparire per questo errore.
  const hideDevOverlay = () => {
    const iframes = document.querySelectorAll('iframe#webpack-dev-server-client-overlay, iframe[src*="webpack-dev-server"]');
    iframes.forEach((f) => {
      try {
        const doc = f.contentDocument || f.contentWindow?.document;
        const txt = doc?.body?.innerText || "";
        if (RO_MSG.test(txt)) f.style.display = "none";
      } catch (_) { /* cross-origin ignore */ }
    });
  };
  const mo = new MutationObserver(hideDevOverlay);
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  else document.addEventListener("DOMContentLoaded", () => mo.observe(document.body, { childList: true, subtree: true }));
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
startAutoTranslate();
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Registra il service worker PWA (produzione + preview). Necessario per notifiche push.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .catch(() => {});
    // Naviga quando il SW ci notifica un click su notifica
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event?.data?.type === "navigate" && event.data.url) {
        try { window.location.href = event.data.url; } catch (_) {}
      }
    });
  });
}
